import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Send, Gift, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: "service", label: "Service", desc: "Une prestation offerte (audit, consultation, accompagnement)" },
  { value: "produit", label: "Produit", desc: "Un produit physique ou digital proposé" },
  { value: "outil", label: "Outil", desc: "Accès à un outil, logiciel ou template" },
  { value: "autre", label: "Autre", desc: "Toute autre forme d'avantage" },
];

export default function CreateBenefit() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", type: "service", value: "", conditions: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/benefits/mine`, { withCredentials: true })
      .then(({ data }) => {
        // If active (pending or approved) benefit exists, redirect to profile
        if (data && (data.status === "pending" || data.status === "approved")) {
          navigate("/mon-profil#mon-avantage", { replace: true });
        }
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/benefits`, form, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => navigate("/mon-profil#mon-avantage"), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="cbm-skeleton h-8 w-32 mb-6 rounded" />
      <div className="cbm-skeleton h-72 rounded-xl" />
    </div>
  );

  if (success) return (
    <div className="p-6 max-w-2xl mx-auto animate-fade-in-up" data-testid="benefit-success-screen">
      <div className="cbm-card p-8 text-center">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(100,200,140,0.15)", border: "1px solid rgba(100,200,140,0.3)" }}>
          <CheckCircle2 size={28} className="text-green-400" />
        </div>
        <h1 className="font-outfit font-bold text-white text-xl mb-2">Avantage soumis !</h1>
        <p className="text-cbm-secondary text-sm">Votre avantage est en attente de validation par l'administrateur.</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-2xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
            <Gift size={18} className="text-gold" />
          </div>
          <div>
            <h1 className="cbm-section-title leading-tight">Proposer un avantage</h1>
            <p className="text-cbm-muted text-xs mt-0.5">Offre exclusive réservée aux membres du club</p>
          </div>
        </div>
        <div className="text-right">
          <p className="cbm-label">Quota mensuel</p>
          <p className="font-outfit font-bold text-gold">
            1 / 1 restante
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="cbm-card p-6 space-y-4 animate-fade-in-up" data-testid="create-benefit-form">
        <div>
          <label className="cbm-label block mb-1.5">Titre de l'avantage *</label>
          <input
            data-testid="benefit-title-input"
            className="cbm-input"
            placeholder="Ex : Audit digital offert pour les membres CBM"
            value={form.title}
            maxLength={120}
            onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
            required
          />
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Type *</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TYPES.map(t => (
              <button
                type="button"
                key={t.value}
                data-testid={`benefit-type-${t.value}`}
                onClick={() => setForm(p => ({ ...p, type: t.value }))}
                className={`text-left p-3 rounded-lg border transition-all ${
                  form.type === t.value
                    ? "border-gold bg-gold/10"
                    : "border-white/10 hover:border-white/20 bg-transparent"
                }`}
                title={t.desc}
              >
                <p className={`font-outfit font-semibold text-sm ${form.type === t.value ? "text-gold" : "text-white"}`}>{t.label}</p>
                <p className="text-cbm-muted text-xs mt-0.5 line-clamp-2">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Valeur *</label>
          <input
            data-testid="benefit-value-input"
            className="cbm-input"
            placeholder="Ex : -30%, Gratuit (valeur 5000 MAD), 1h de consultation offerte"
            value={form.value}
            maxLength={80}
            onChange={(e) => setForm(p => ({ ...p, value: e.target.value }))}
            required
          />
          <p className="text-cbm-muted text-xs mt-1">Texte court, bien visible sur la card publique.</p>
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Description *</label>
          <textarea
            data-testid="benefit-description-input"
            className="cbm-input"
            rows={4}
            placeholder="Décrivez précisément l'avantage offert aux membres..."
            value={form.description}
            maxLength={600}
            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
            required
          />
          <p className="text-cbm-muted text-xs mt-1">{form.description.length}/600</p>
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Conditions (optionnel)</label>
          <textarea
            data-testid="benefit-conditions-input"
            className="cbm-input"
            rows={2}
            placeholder="Ex : Réservé aux membres CBM, engagement minimum 3 mois, sur présentation du profil..."
            value={form.conditions}
            maxLength={300}
            onChange={(e) => setForm(p => ({ ...p, conditions: e.target.value }))}
          />
        </div>

        {error && <p className="text-red-400 text-sm bg-red-900/20 rounded-lg px-3 py-2" data-testid="benefit-error">{error}</p>}

        <div className="flex items-center justify-between pt-2 border-t cbm-divider">
          <p className="text-cbm-muted text-xs">Votre avantage sera publié après validation par l'administrateur.</p>
          <button
            data-testid="submit-benefit-button"
            type="submit"
            className="cbm-btn-primary text-sm"
            disabled={submitting}
          >
            {submitting ? "Envoi..." : <><Send size={14} /> Publier mon avantage</>}
          </button>
        </div>
      </form>
    </div>
  );
}
